import type { QueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useAPIErrorHandler } from '../../../../../../components/hooks/useAPIErrorHandler';
import { getOwnershipTypes } from '../../../../../../api';
import { getData } from '../../../../../../utils/functional/fp';
import type { SelectOption } from '../../customizeQuote/reducer/types';
import { FEATURE_FLAG_KEYS } from '../../../../../../config/growthbook';

export const useFetchOwnershipTypes = () => {
  const handleAPIError = useAPIErrorHandler();
  const isOwnershipTypeEnabled = useFeatureIsOn(
    FEATURE_FLAG_KEYS.OWNERSHIP_TYPE
  );

  const query = useQuery({
    queryKey: ['ownershipTypes'],
    queryFn: getOwnershipTypes,
    select: getData,
    onError: handleAPIError('Unable to fetch Ownership type'),
    cacheTime: Infinity,
    staleTime: 3600000,
    enabled: isOwnershipTypeEnabled,
  } as QueryOptions);

  const ownershipTypeOptions = isOwnershipTypeEnabled
    ? query?.data ?? []
    : OWNERSHIP_TYPE_OPTIONS;

  const ownershipTypeOptionsMap: Record<string, SelectOption<string>> = useMemo(
    () =>
      ((query?.data as Array<SelectOption<string>>) ?? [])?.reduce(
        (accumulator, currentValue) => {
          accumulator[currentValue.value] = currentValue;
          return accumulator;
        },
        {} as Record<string, SelectOption<string>>
      ),
    [query?.data]
  );

  const getOwnershipTypeLabel = (value?: string) =>
    isOwnershipTypeEnabled
      ? value && ownershipTypeOptionsMap?.[value]?.label
      : OWNERSHIP_TYPE_OPTIONS.find((type) => type.value === value)?.label;

  return {
    query,
    ownershipTypeOptions: ownershipTypeOptions as Array<SelectOption<string>>,
    ownershipTypeOptionsMap,
    getOwnershipTypeLabel,
  };
};

const OWNERSHIP_TYPE_OPTIONS: Array<SelectOption<string>> = [
  {
    label: 'Private',
    value: 'Private',
  },
  {
    label: 'Publicly Traded',
    value: 'Public Sector',
  },
  {
    label: 'Investment Fund',
    value: 'Partnership',
  },
  {
    label: 'Not For Profit',
    value: 'Non-Profit',
  },
  {
    label: 'Government Entity',
    value: 'Non-Corporates',
  },
];
